<template>
  <div class="login-container">
    <div class="login-card">
      <!-- Header -->
      <div class="login-header">
        <div class="logo-container">
          <img src="https://res.cloudinary.com/dzrldiajp/image/upload/w_400/v1765834427/vbzyvyi1lif0ub6dk9x2.png" alt="Logo" class="login-logo">
        </div>
        <h1 class="login-title">
          <span class="brand-text">cash.co.zm</span>
        </h1>
        <p class="login-subtitle">Sign in to your account</p>
      </div>

      <!-- Login Form -->
      <v-form ref="form" v-model="valid" @submit.prevent="" class="login-form">
        <div class="form-group">
          <label class="form-label">Username</label>
          <v-text-field
            v-model="username"
            :rules="usernameRules"
            variant="outlined"
            density="comfortable"
            hide-details="auto"
            required
            autocomplete="username"
            class="form-field"
          ></v-text-field>
        </div>

        <div class="form-group">
          <label class="form-label">Password</label>
          <v-text-field
            v-model="password"
            :rules="passwordRules"
            variant="outlined"
            density="comfortable"
            :append-inner-icon="showPassword ? 'mdi-eye-off' : 'mdi-eye'"
            :type="showPassword ? 'text' : 'password'"
            hide-details="auto"
            required
            autocomplete="current-password"
            @click:append-inner="showPassword = !showPassword"
            class="form-field"
          ></v-text-field>
        </div>

        <div class="form-actions">
          <v-btn
            variant="text"
            color="primary"
            size="small"
            class="forgot-password-btn"
            @click="initiatePasswordReset()"
          >
            Forgot password?
          </v-btn>
        </div>

        <!-- Login Button -->
        <v-btn
          block
          color="primary"
          size="large"
          type="submit"
          :loading="loading"
          :disabled="!valid"
          class="login-button"
          @click="login"
        >
          Sign In
        </v-btn>
      </v-form>

      <!-- Error Alert -->
      <div v-if="error" class="error-section">
        <v-alert
          type="error"
          variant="tonal"
          closable
          density="compact"
          class="error-alert"
        >
          {{ error }}
        </v-alert>
      </div>
    </div>

    <!-- Reset Password Modal -->
    <CustomModal
      ref="resetPasswordModal"
      title="Reset Password"
      size="sm"
      :is-form="true"
      @open="handleResetModalOpen"
      @close="handleResetModalClose"
      @submit="initiatePasswordReset"
    >
      <div class="reset-content">
        <p class="reset-description">
          Enter your contact number to receive password reset instructions.
        </p>

        <v-form ref="resetForm" v-model="resetValid">
          <div class="form-group">
            <label class="form-label">Phone Number</label>
            <v-text-field
              v-model="resetUsername"
              type="number"
              variant="outlined"
              density="comfortable"
              hide-details="auto"
              required
              class="form-field"
            ></v-text-field>
          </div>
        </v-form>
      </div>

      <template #footer>
        <v-spacer></v-spacer>
        <v-btn
          variant="text"
          color="secondary"
          @click="$refs.resetPasswordModal.closeModal()"
        >
          Cancel
        </v-btn>
        <v-btn
          color="primary"
          variant="flat"
          @click="initiatePasswordReset"
          :disabled="!resetValid"
          :loading="resetting"
        >
          Reset Password
        </v-btn>
      </template>
    </CustomModal>
  </div>
</template>

<script>
import axios from 'axios';
import CustomModal from '../../components/utils/CustomModal.vue';

export default {
  name: 'HelpDeskLogin',
  components: {
    CustomModal
  },
  data() {
    return {
      username: '',
      password: '',
      showPassword: false,
      loading: false,
      valid: true,
      error: null,

      // Reset password data
      resetUsername: '',
      resetValid: true,
      resetting: false,
      resetSuccess: false,
      resetError: null,

      // Validation rules
      usernameRules: [
        v => !!v || 'Username is required',
        v => (v && v.length >= 3) || 'Username must be at least 3 characters'
      ],
      passwordRules: [
        v => !!v || 'Password is required'
      ],
      emailRules: [
        v => !!v || 'Email is required',
        v => /.+@.+\..+/.test(v) || 'Email must be valid'
      ]
    };
  },
  methods: {
    async login() {
      if (!this.$refs.form.validate()) return;

      this.loading = true;
      this.error = null;

      window.$analytics?.trackAuth('login_attempt', { username: this.username });

      try {
        const payload = {
          username: this.username,
          password: this.password
        };

        const authData = { ...payload };
        this.$store.commit('setAuth', authData);

        const loginResponse = await this.$store.dispatch("logIn", payload);

        window.$analytics?.trackAuth('login_success');

        // Check if backend returned a prefilled OTP (for special test accounts)
        if (loginResponse && loginResponse.otp) {
          console.log('[LoginForm] Received prefilled OTP from backend:', loginResponse.otp);
          this.$store.commit('setAuth', { ...authData, prefilledOTP: loginResponse.otp });
        }

        this.$router.push({ name: 'OTPForm' });
      } catch (error) {
        console.error('Login failed:', error);
        window.$analytics?.trackAuth('login_failure', { error: error?.message || 'unknown' });
        this.error = error.message || 'Login failed. Please check your credentials and try again.';
      } finally {
        this.loading = false;
      }
    },

    forgotPassword() {
      this.$router.push({ name: 'ForgotPassword' });
    },

    handleResetModalOpen() {
      this.$nextTick(() => {
        if (this.$refs.resetForm) {
          this.$refs.resetForm.resetValidation();
        }
      });
    },

    handleResetModalClose() {
      this.resetUsername = '';
      this.resetError = null;
      this.resetSuccess = false;
    },

    async initiatePasswordReset(){
      this.$router.push({ name: 'ForgotPassword' });
    },
  }
};
</script>

<style scoped lang="scss">
.login-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background-color: rgb(var(--v-theme-background));
  padding: 20px;
}

.login-card {
  background: rgb(var(--v-theme-surface));
  border-radius: 12px;
  border: 1px solid rgb(var(--v-theme-border-color));
  padding: 48px 40px;
  width: 100%;
  max-width: 420px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.login-header {
  text-align: center;
  margin-bottom: 32px;
}

.logo-container {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 16px;
  min-height: 80px;
}

.login-logo {
  max-width: 200px;
  max-height: 80px;
  width: auto;
  height: auto;
  object-fit: contain;

  &.fallback {
    display: none;
  }
}

.login-title {
  font-size: 28px;
  font-weight: 600;
  color: rgb(var(--v-theme-text-primary));
  margin: 0 0 8px 0;
}

.brand-text {
  color: rgb(var(--v-theme-primary));
}

.login-subtitle {
  font-size: 14px;
  color: rgb(var(--v-theme-text-secondary));
  margin: 0;
  font-weight: 400;
}

.login-form {
  .form-group {
    margin-bottom: 20px;
  }

  .form-label {
    display: block;
    font-size: 14px;
    font-weight: 500;
    color: rgb(var(--v-theme-text-primary));
    margin-bottom: 6px;
  }

  .form-field {
    :deep(.v-field) {
      border-radius: 8px;
      border: 1px solid rgb(var(--v-theme-border-color));
      background-color: rgb(var(--v-theme-surface));

      &:hover {
        border-color: rgba(var(--v-theme-on-surface), 0.5);
      }

      &.v-field--focused {
        border-color: rgb(var(--v-theme-primary));
        box-shadow: 0 0 0 2px rgba(var(--v-theme-primary), 0.1);
      }

      &.v-field--error {
        border-color: rgb(var(--v-theme-error));
      }
    }

    :deep(.v-field__input) {
      font-size: 14px;
      padding: 12px 16px;
      min-height: 48px;
    }

    :deep(.v-field__append-inner) {
      padding-top: 0;
      align-items: center;

      .v-icon {
        color: rgb(var(--v-theme-text-secondary));
        font-size: 18px;
        cursor: pointer;

        &:hover {
          color: rgb(var(--v-theme-text-primary));
        }
      }
    }
  }

  .form-actions {
    display: flex;
    justify-content: flex-end;
    margin-bottom: 24px;
  }

  .forgot-password-btn {
    font-size: 13px;
    font-weight: 500;
    text-transform: none;
    color: rgb(var(--v-theme-primary));
    padding: 4px 8px;
    min-width: auto;

    &:hover {
      background-color: rgba(var(--v-theme-primary), 0.04);
    }
  }

  .login-button {
    height: 48px;
    font-size: 14px;
    font-weight: 500;
    text-transform: none;
    border-radius: 8px;
    box-shadow: none;

    &:hover {
      box-shadow: 0 2px 4px rgba(var(--v-theme-primary), 0.2);
    }

    &:disabled {
      opacity: 0.6;
    }
  }
}

.error-section {
  margin-top: 20px;

  .error-alert {
    border-radius: 8px;
    font-size: 13px;
  }
}

// Reset modal styles
.reset-content {
  .reset-description {
    font-size: 14px;
    color: rgb(var(--v-theme-text-secondary));
    margin-bottom: 20px;
    line-height: 1.4;
  }
}

// Responsive adjustments
@media (max-width: 600px) {
  .login-container {
    padding: 16px;
  }

  .login-card {
    padding: 32px 24px;
    max-width: 100%;
  }

  .logo-container {
    margin-bottom: 12px;
    min-height: 60px;
  }

  .login-logo {
    max-width: 160px;
    max-height: 60px;
  }

  .login-title {
    font-size: 24px;
  }
}

// Remove Vuetify's default field styling that might interfere
:deep(.v-messages) {
  font-size: 12px;
  color: rgb(var(--v-theme-error));
  margin-top: 4px;
}

:deep(.v-field--variant-outlined .v-field__outline) {
  --v-field-border-width: 1px;
  --v-field-border-opacity: 1;
}
</style>
